class CustomSlider extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('.custom-slider__grid');
    this.prevButton = this.querySelector('.custom-slider__button--prev');
    this.nextButton = this.querySelector('.custom-slider__button--next');
    this.sliderItems = this.querySelectorAll('.custom-slider__item');
    
    // Get data attributes for configuration
    this.mobileGap = parseInt(this.dataset.mobileGap) || 32;
    this.desktopGap = parseInt(this.dataset.desktopGap) || 100;
    
    // State variables
    this.isDown = false;
    this.startX = 0;
    this.scrollLeft = 0;
    this.resizeTimeout = null;
    
    // Bind methods to preserve 'this' context
    this.handlePrevClick = this.handlePrevClick.bind(this);
    this.handleNextClick = this.handleNextClick.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleResize = this.handleResize.bind(this);
  }

  connectedCallback() {
    if (!this.slider) return;
    if (!this.prevButton || !this.nextButton) return;
    if (this.sliderItems.length === 0) return;

    // Add event listeners
    this.prevButton.addEventListener('click', this.handlePrevClick);
    this.nextButton.addEventListener('click', this.handleNextClick);
    this.slider.addEventListener('scroll', this.handleScroll);
    this.slider.addEventListener('mousedown', this.handleMouseDown);
    this.slider.addEventListener('mouseleave', this.handleMouseLeave);
    this.slider.addEventListener('mouseup', this.handleMouseUp);
    this.slider.addEventListener('mousemove', this.handleMouseMove);
    window.addEventListener('resize', this.handleResize);

    // Initial update
    this.updateButtons();
  }

  disconnectedCallback() {
    // Remove event listeners
    if (this.prevButton) {
      this.prevButton.removeEventListener('click', this.handlePrevClick);
    }
    if (this.nextButton) {
      this.nextButton.removeEventListener('click', this.handleNextClick);
    }
    if (this.slider) {
      this.slider.removeEventListener('scroll', this.handleScroll);
      this.slider.removeEventListener('mousedown', this.handleMouseDown);
      this.slider.removeEventListener('mouseleave', this.handleMouseLeave);
      this.slider.removeEventListener('mouseup', this.handleMouseUp);
      this.slider.removeEventListener('mousemove', this.handleMouseMove);
    }
    window.removeEventListener('resize', this.handleResize);
    
    // Clear timeout if exists
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
  }

  updateButtons() {
    if (!this.slider) return;
    
    const scrollLeft = this.slider.scrollLeft;
    const scrollWidth = this.slider.scrollWidth;
    const clientWidth = this.slider.clientWidth;
    const scrollRight = scrollWidth - scrollLeft - clientWidth;

    if (this.prevButton) {
      if (scrollLeft <= 5) {
        this.prevButton.classList.add('disabled');
      } else {
        this.prevButton.classList.remove('disabled');
      }
    }

    if (this.nextButton) {
      if (scrollRight <= 5) {
        this.nextButton.classList.add('disabled');
      } else {
        this.nextButton.classList.remove('disabled');
      }
    }
  }

  getItemWidth() {
    const isMobile = window.innerWidth <= 920;
    const gap = isMobile ? this.mobileGap : this.desktopGap;
    if (this.sliderItems.length === 0) return 0;
    return this.sliderItems[0].offsetWidth + gap;
  }

  handlePrevClick() {
    if (!this.slider) return;
    this.slider.scrollBy({
      left: -this.getItemWidth(),
      behavior: 'smooth'
    });
  }

  handleNextClick() {
    if (!this.slider) return;
    this.slider.scrollBy({
      left: this.getItemWidth(),
      behavior: 'smooth'
    });
  }

  handleScroll() {
    if (!this.slider) return;
    if (this.slider.scrollLeft < 0) {
      this.slider.scrollLeft = 0;
    }
    this.updateButtons();
  }

  handleMouseDown(e) {
    if (!this.slider) return;
    this.isDown = true;
    this.slider.style.cursor = 'grabbing';
    this.startX = e.pageX - this.slider.offsetLeft;
    this.scrollLeft = this.slider.scrollLeft;
  }

  handleMouseLeave() {
    if (!this.slider) return;
    this.isDown = false;
    this.slider.style.cursor = 'grab';
  }

  handleMouseUp() {
    if (!this.slider) return;
    this.isDown = false;
    this.slider.style.cursor = 'grab';
  }

  handleMouseMove(e) {
    if (!this.isDown || !this.slider) return;
    e.preventDefault();
    const x = e.pageX - this.slider.offsetLeft;
    const walk = (x - this.startX) * 2;
    this.slider.scrollLeft = this.scrollLeft - walk;
  }

  handleResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.updateButtons();
    }, 100);
  }
}

// Initialize the custom component if it is not initialized
if (!window.customElements.get('custom-slider')) {
  window.customElements.define('custom-slider', CustomSlider);
}
